import React from 'react';
import { Clock, Coffee } from 'lucide-react';
import { ShiftInfoTableData } from '@/types/shifts';
import { getShiftDetailsForDay } from '@/utils/shiftFormUtils';
import { getStatusColor } from '@/utils/statusUtils';

interface ShiftInfoTableProps {
  shiftData: ShiftInfoTableData;
  otDate: string;
  className?: string;
  showNoWorkingHoursMessage?: boolean;
}

export const ShiftInfoTable: React.FC<ShiftInfoTableProps> = ({
  shiftData,
  otDate,
  className = '',
  showNoWorkingHoursMessage = false
}) => {
  // Format time for display (HH:MM format)
  const formatTime = (time: string): string => {
    if (!time) return '--:--';
    // Handle both HH:MM:SS and HH:MM formats
    const timeParts = time.split(':');
    return `${timeParts[0]}:${timeParts[1]}`;
  };

  // Format break hours for display
  const formatBreakHours = (breakHrs: number | null): string => {
    if (breakHrs === null || breakHrs === undefined) return 'Not specified';
    if (breakHrs === 0) return 'No break';
    return `${breakHrs} hour${breakHrs === 1 ? '' : 's'}`;
  };

  // Calculate shift duration
  const calculateShiftDuration = (startTime: string, endTime: string): string => {
    if (!startTime || !endTime) return '--';
    
    try {
      const start = new Date(`1970-01-01T${startTime}`);
      const end = new Date(`1970-01-01T${endTime}`);
      
      // Handle overnight shifts
      if (end < start) {
        end.setDate(end.getDate() + 1);
      }
      
      const diffMs = end.getTime() - start.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      
      return `${diffHours.toFixed(1)} hours`;
    } catch {
      return '--';
    }
  };

  // Get shift details for the specific OT date
  const getShiftDetailsForOTDate = () => {
    if (!otDate || !shiftData?.day_time_deduction) return null;
    const otDateObj = new Date(otDate);
    return getShiftDetailsForDay(shiftData.day_time_deduction, otDateObj);
  };

  const shiftForDate = getShiftDetailsForOTDate();

  return (
    <div className={`${className} w-full space-y-2`}>
      {/* Shift Information Table */}
      <div className="w-full rounded-md border border-slate-200 dark:border-slate-600 overflow-hidden">
        <div className="w-full">
          <table className="w-full table-fixed text-xs whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-600">
                <th className="px-1.5 py-1 text-left text-[11px] font-medium text-slate-700 dark:text-slate-300">Shift</th>
                <th className="px-1.5 py-1 text-left text-[11px] font-medium text-slate-700 dark:text-slate-300">Timing</th>
                <th className="px-1.5 py-1 text-left text-[11px] font-medium text-slate-700 dark:text-slate-300">Duration</th>
                <th className="px-1.5 py-1 text-left text-[11px] font-medium text-slate-700 dark:text-slate-300">Break</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white dark:bg-slate-800/50">
                <td className="px-1.5 py-1.5">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">
                      {shiftData.name}
                    </span>
                  </div>
                </td>
                <td className="px-1.5 py-1.5">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">
                      {shiftForDate ? `${formatTime(shiftForDate.start_time)} - ${formatTime(shiftForDate.end_time)}` : '--'}
                    </span>
                  </div>
                </td>
                <td className="px-1.5 py-1.5">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">
                      {shiftForDate ? calculateShiftDuration(shiftForDate.start_time, shiftForDate.end_time) : '--'}
                    </span>
                  </div>
                </td>
                <td className="px-1.5 py-1.5">
                  <div className="flex items-center gap-1">
                    <Coffee className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">
                      {shiftForDate ? formatBreakHours(shiftForDate.break_hrs) : '--'}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* No Working Hours Message - Only show if requested */}
      {showNoWorkingHoursMessage && !shiftForDate && (
        <div className={`flex items-center gap-1.5 px-2 py-1 border rounded text-xs ${getStatusColor('info', 'light')}`}>
          <span>No working hours on this date - OT can be anytime</span>
        </div>
      )}
    </div>
  );
};

export default ShiftInfoTable;
